import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Table } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import StatCard from "../../../../components/Cards/StatCard";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { del, get } from "../../../../utils/helpers/api";
import { AWS_S3_URL } from "../../../../utils/constants";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";
import { toast } from "react-toastify";
import {
  IoArrowDownCircleOutline,
  IoArrowUpCircleOutline,
  IoCreateOutline,
  IoCubeOutline,
  IoLayersOutline,
  IoTrashOutline,
  IoWalletOutline
} from "react-icons/io5";
import { PiDotsThreeOutlineVerticalFill } from "react-icons/pi";

function fmt(n) {
  return parseFloat(n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ViewProductPage() {
  const { t } = useTranslation();
  const { settings, formatDate } = useSettings();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [productData, setProductData] = useState(state?.productData || {});
  const [stockLevels, setStockLevels] = useState([]);
  const [movements, setMovements] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const currency = settings?.sales?.base_currency || '$';

  useEffect(() => {
    if (id) {
      getProduct();
      getMovements();
    }
  }, [id]);

  const getProduct = async () => {
    const data = await get('/inventory/products/view/' + id);
    if (data?.data) setProductData(data.data);
    // current_stock is only the company-wide total now; this is where the goods
    // actually are.
    if (data?.stock_levels) setStockLevels(data.stock_levels);
  };

  const getMovements = async () => {
    const data = await get('/inventory/products/movements/' + id);
    if (data?.movements) setMovements(data.movements.data ?? []);
  };

  const deleteProduct = async () => {
    const data = await del('/inventory/products/delete/' + id);
    if (data?.success) {
      toast.success(data.message || t('inventory.productDeleted'));
      navigate('/inventory/products');
    } else {
      toast.error(data?.message || t('common.somethingWentWrong'));
    }
    setDeleteOpen(false);
  };

  const isService = productData.type === 'service';
  const isOutOfStock = !isService && Number(productData.current_stock ?? 0) === 0;
  const isLowStock = !isService && !isOutOfStock && productData.minimum_stock != null
    && Number(productData.current_stock ?? 0) <= Number(productData.minimum_stock);

  // Valued at selling price, the same basis the inventory dashboard uses, so
  // the two never quote different numbers for the same goods.
  const stockValue = useMemo(
    () => parseFloat(productData.selling_price ?? 0) * parseFloat(productData.current_stock ?? 0),
    [productData.selling_price, productData.current_stock]
  );

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={productData.name} onBack={() => navigate('/inventory/products')}>
          <Button
            size="sm"
            outline
            color="secondary"
            className="d-flex align-items-center gap-1"
            onClick={() => navigate('/inventory/products/edit/' + id, { state: { productData } })}
          >
            <IoCreateOutline size={14} />
            {t('common.edit')}
          </Button>
          <Dropdown isOpen={dropdownOpen} toggle={() => setDropdownOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem className="text-danger" onClick={() => setDeleteOpen(true)}>
                <IoTrashOutline size={15} /> {t('common.delete')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content scrollable">
          {/* Profile Card */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex align-items-center gap-4">
                {productData.image_path ? (
                  <img
                    src={AWS_S3_URL + productData.image_path}
                    alt={productData.name}
                    className="rounded flex-shrink-0"
                    style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #e9ecef' }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
                    style={{ width: 64, height: 64, background: '#f0eeff' }}
                  >
                    <IoCubeOutline size={26} color="#6C63FF" />
                  </div>
                )}
                <div>
                  <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                    <h5 className="mb-0 fw-bold">{productData.name}</h5>
                    <Badge color="secondary" className="text-capitalize">
                      {t('inventory.' + (productData.type || 'product'))}
                    </Badge>
                    {isOutOfStock && <Badge color="danger">{t('inventory.outOfStock')}</Badge>}
                    {isLowStock && <Badge color="warning">{t('inventory.lowStock')}</Badge>}
                  </div>
                  <div className="d-flex flex-wrap gap-3 text-muted" style={{ fontSize: '0.85rem' }}>
                    {productData.category?.name && <span>{productData.category.name}</span>}
                    {productData.sku && <span>{t('inventory.sku')}: {productData.sku}</span>}
                    {productData.barcode && <span>{t('inventory.barcode')}: {productData.barcode}</span>}
                  </div>
                  {productData.description && (
                    <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.85rem' }}>{productData.description}</p>
                  )}
                </div>
              </div>
            </CardBody>
          </Card>

          {!isService && stockLevels.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">{t('warehouses.stockByWarehouse')}</h6>
              <Table bordered responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{t('warehouses.warehouse')}</th>
                    <th className="text-end">{t('warehouses.onHand')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stockLevels.map((level) => (
                    <tr key={level.warehouse_id}>
                      <td>{level.warehouse_name}</td>
                      <td className="text-end">{Number(level.quantity)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td><strong>{t('warehouses.totalAcrossWarehouses')}</strong></td>
                    <td className="text-end">
                      <strong>{stockLevels.reduce((sum, l) => sum + Number(l.quantity), 0)}</strong>
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}

          {/* Stats */}
          <Row className="g-3 mb-4">
            {!isService && (
              <Col md="3">
                <StatCard
                  icon={<IoLayersOutline size={18} color={isOutOfStock ? '#fa5252' : isLowStock ? '#f76707' : '#6C63FF'} />}
                  iconBg={isOutOfStock ? '#fff0f0' : isLowStock ? '#fff4e6' : '#f0eeff'}
                  value={productData.current_stock ?? 0}
                  label={t('inventory.currentStock')}
                />
              </Col>
            )}
            <Col md={isService ? 4 : 3}>
              <StatCard
                icon={<IoWalletOutline size={18} color="#20c997" />}
                iconBg="#e6faf5"
                value={`${currency} ${fmt(productData.selling_price)}`}
                label={t('inventory.sellingPrice')}
              />
            </Col>
            {!isService && (
              <>
                <Col md="3">
                  <StatCard
                    icon={<IoWalletOutline size={18} color="#845ef7" />}
                    iconBg="#f3f0ff"
                    value={`${currency} ${fmt(productData.cost_price)}`}
                    label={t('inventory.costPrice')}
                  />
                </Col>
                <Col md="3">
                  <StatCard
                    icon={<IoWalletOutline size={18} color="#2f9e44" />}
                    iconBg="#ebfbee"
                    value={`${currency} ${fmt(stockValue)}`}
                    label={`${t('inventory.totalStockValue')} — ${t('inventory.atSellingPrice')}`}
                  />
                </Col>
              </>
            )}
          </Row>

          {/* Weight & Dimensions */}
          {!isService && (
            <Card className="mb-4">
              <CardHeader className="bg-white">
                <CardTitle tag="h6" className="mb-0 fw-bold">{t('inventory.dimensions')}</CardTitle>
              </CardHeader>
              <CardBody>
                <Row className="g-3">
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.grossWeight')}</div>
                    <div className="fw-semibold">
                      {productData.gross_weight ? `${productData.gross_weight} ${productData.unit_weight}` : '-'}
                    </div>
                  </Col>
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.netWeight')}</div>
                    <div className="fw-semibold">
                      {productData.net_weight ? `${productData.net_weight} ${productData.unit_weight}` : '-'}
                    </div>
                  </Col>
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.length')}</div>
                    <div className="fw-semibold">{productData.product_length || '-'}</div>
                  </Col>
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.width')}</div>
                    <div className="fw-semibold">{productData.product_width || '-'}</div>
                  </Col>
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.height')}</div>
                    <div className="fw-semibold">{productData.product_height || '-'}</div>
                  </Col>
                  <Col md="2">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{t('inventory.volume')}</div>
                    <div className="fw-semibold">{productData.volume || 0} {productData.unit_of_measure}³</div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Recent Stock Movements */}
          {!isService && (
            <Card className="mb-4">
              <CardHeader className="bg-white d-flex align-items-center justify-content-between">
                <CardTitle tag="h6" className="mb-0 fw-bold">{t('inventory.recentStockMovements')}</CardTitle>
                <Button
                  color="link"
                  size="sm"
                  className="text-muted p-0"
                  onClick={() => navigate('/inventory/stock-movements')}
                >
                  {t('common.viewAll')}
                </Button>
              </CardHeader>
              {movements.length === 0 ? (
                <CardBody>
                  <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>{t('inventory.noStockMovements')}</p>
                </CardBody>
              ) : (
                movements.map((m, i) => (
                  <div
                    key={m.id}
                    className="sales-panel-item d-flex align-items-center gap-3 px-3 py-2"
                    style={{ borderTop: i === 0 ? '1px solid #f0f0f0' : undefined }}
                  >
                    <div
                      className="d-flex align-items-center justify-content-center rounded flex-shrink-0"
                      style={{ width: 36, height: 36, background: m.type === 'in' ? '#e6faf5' : '#fff0f0' }}
                    >
                      {m.type === 'in'
                        ? <IoArrowUpCircleOutline size={18} color="#20c997" />
                        : <IoArrowDownCircleOutline size={18} color="#fa5252" />}
                    </div>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-semibold text-truncate" style={{ fontSize: '0.84rem' }}>
                        {m.reference || t('inventory.stockMovement')}
                      </div>
                      <div className="text-muted text-truncate" style={{ fontSize: '0.75rem' }}>
                        {formatDate(m.created_at)} &middot; {m.previous_stock} &rarr; {m.new_stock}
                      </div>
                    </div>
                    <span
                      className="fw-bold flex-shrink-0"
                      style={{ fontSize: '0.84rem', color: m.type === 'in' ? '#20c997' : '#fa5252' }}
                    >
                      {m.type === 'in' ? '+' : '-'}{m.quantity}
                    </span>
                  </div>
                ))
              )}
            </Card>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteOpen}
        title={t('inventory.deleteProduct')}
        message={t('inventory.deleteProductConfirm')}
        onConfirm={deleteProduct}
        toggle={() => setDeleteOpen(false)}
      />
    </SidebarLayout>
  );
}

export default ViewProductPage;
